/* Homepage entrances. Native scrolling and the carousel track remain independent. */
(() => {
  if (!document.body.classList.contains('template-index') || !('IntersectionObserver' in window)) return;

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const stories = new Map();
  const prepared = new WeakSet();
  const observers = new Map();
  const reveal = (element, immediate = false) => {
    observers.get(element)?.unobserve(element);
    observers.delete(element);
    if (immediate) element.classList.add('is-immediate');
    element.classList.remove('is-pending');
    element.dataset.brewnoRevealed = 'true';
    const story = stories.get(element.closest('.brewno-story p'));
    if (story) story.started = true;
  };
  const onIntersect = (entries) => entries.forEach((entry) => {
    const threshold = entry.target.classList.contains('brewno-reveal-line') ? 0.45 : 0.15;
    if (entry.isIntersecting && entry.intersectionRatio >= threshold) reveal(entry.target);
  });
  const lineObserver = new IntersectionObserver(onIntersect, { threshold: 0.45, rootMargin: '0px 0px -6% 0px' });
  const blockObserver = new IntersectionObserver(onIntersect, { threshold: 0.15, rootMargin: '0px 0px -6% 0px' });

  function prepare(element, delay = 0, line = false, immediate = false) {
    element.classList.add('brewno-reveal');
    element.style.setProperty('--brewno-reveal-delay', `${delay}ms`);
    if (line) element.classList.add('brewno-reveal-line');
    if (immediate || reducedMotion.matches || element.getBoundingClientRect().bottom <= 0) {
      reveal(element, true);
      return;
    }
    const observer = line ? lineObserver : blockObserver;
    observers.set(element, observer);
    observer.observe(element);
    element.classList.add('is-pending');
  }

  function splitStory(paragraph, force = false) {
    const story = stories.get(paragraph);
    const width = paragraph.getBoundingClientRect().width;
    if (!width || (!force && Math.abs(width - story.width) < 1)) return;
    story.width = width;

    // Measure natural wrapping with the real font; preserve merchant line breaks.
    const measure = story.original.cloneNode(true);
    measure.removeAttribute('id');
    measure.setAttribute('aria-hidden', 'true');
    Object.assign(measure.style, {
      position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
      top: '0', left: '0', width: `${width}px`, margin: '0'
    });
    paragraph.parentElement.append(measure);
    const walker = document.createTreeWalker(measure, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    const lines = [];
    let top;
    let text = '';
    let node;
    while ((node = walker.nextNode())) {
      for (let index = 0; index < node.length; index++) {
        range.setStart(node, index);
        range.setEnd(node, index + 1);
        const rect = range.getBoundingClientRect();
        if (rect.width && top !== undefined && Math.abs(rect.top - top) > 2) {
          if (text.trim()) lines.push(text.trim());
          text = '';
        }
        text += node.data[index];
        if (rect.width) top = rect.top;
      }
    }
    if (text.trim()) lines.push(text.trim());
    measure.remove();
    if (!lines.length) return;

    paragraph.querySelectorAll('.brewno-reveal').forEach((line) => {
      observers.get(line)?.unobserve(line);
      observers.delete(line);
    });
    const fragment = document.createDocumentFragment();
    lines.forEach((text, index) => {
      const line = document.createElement('span');
      line.textContent = text;
      if (index) fragment.append(document.createTextNode('\n'));
      fragment.append(line);
    });
    paragraph.replaceChildren(fragment);
    [...paragraph.children].forEach((line, index) => prepare(line, Math.min(index * 80, 320), true, story.started));
  }

  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver((entries) => {
    entries.forEach(({ target }) => splitStory(target));
  }) : null;

  function initialize(root = document) {
    if (reducedMotion.matches || window.Shopify?.designMode) return;
    root.querySelectorAll('.brewno-story p').forEach((paragraph) => {
      if (stories.has(paragraph)) return;
      stories.set(paragraph, { original: paragraph.cloneNode(true), width: 0, started: false });
      splitStory(paragraph);
      resizeObserver?.observe(paragraph);
    });
    root.querySelectorAll('.brewno-carousel__heading, .brewno-carousel__viewport, .brewno-footer > div, .brewno-footer > nav').forEach((element) => {
      if (prepared.has(element)) return;
      prepared.add(element);
      const delay = element.classList.contains('brewno-carousel__viewport') ? 80 : 0;
      prepare(element, delay);
    });
  }

  initialize();
  document.fonts?.ready.then(() => stories.forEach((_, paragraph) => splitStory(paragraph, true)));
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) [...observers.keys()].forEach((element) => reveal(element, true));
  });
  document.addEventListener('focusin', (event) => {
    const element = event.target.closest('.brewno-reveal');
    if (element) reveal(element, true);
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) [...observers.keys()].forEach((element) => reveal(element, true));
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    stories.forEach((_, paragraph) => {
      if (event.target.contains(paragraph)) {
        resizeObserver?.unobserve(paragraph);
        stories.delete(paragraph);
      }
    });
    observers.forEach((observer, element) => {
      if (event.target.contains(element)) {
        observer.unobserve(element);
        observers.delete(element);
      }
    });
  });
})();
